import * as React from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';

import { AppShell } from '@/components/app-shell';
import { isPublicPath } from '@/auth/public-paths';
import { useAuth } from '@/auth/auth-context';
import { AdminLandingScreen } from '@/routes/admin-landing-screen';
import { MentionsLegalesScreen } from '@/routes/mentions-legales-screen';
import { AdminDashboardRoute } from '@/routes/admin/admin-dashboard-route';
import { SuppliersListRoute } from '@/routes/admin/suppliers/suppliers-list-route';
import {
  ESTABLISHMENT_STATES,
  SUPPLIER_PAGE_SIZES,
  SUPPLIER_SORT_COLUMNS,
  TOKEN_ACTIVATION_STATUSES,
} from '@/routes/admin/suppliers/suppliers-list-screen';
import type {
  AdminListSortColumn,
  AdminListSortDirection,
  EstablishmentStateDto,
  TokenActivationStatusDto,
} from '@/api/supplier-client';
import { SupplierDetailRoute } from '@/routes/admin/suppliers/supplier-detail-route';
import { SupplierEnrollRoute } from '@/routes/admin/suppliers/supplier-enroll-route';
import { LegalEntityHierarchyRoute } from '@/routes/admin/suppliers/legal-entity-hierarchy-route';
import { CguSignaturesRoute } from '@/routes/admin/cgu/cgu-signatures-route';
import { CguRegistreRoute } from '@/routes/admin/cgu/cgu-registre-route';
import type { CguRegistreTab } from '@/routes/admin/cgu/cgu-registre-screen';
import { ClientsListRoute } from '@/routes/admin/clients/clients-list-route';
import {
  CLIENT_SORT_COLUMNS,
  type ClientSortColumn,
  type ClientSortDirection,
} from '@/routes/admin/clients/clients-list-screen';
import type { ClientStateDto } from '@/api/clients-client';
import { CatalogVersionsRoute } from '@/routes/admin/catalog/catalog-versions-route';
import {
  CatalogEditionRoute,
  validateCatalogEditionSearch,
} from '@/routes/admin/catalog/catalog-edition-route';
import { CatalogSkillsRoute } from '@/routes/admin/catalog/catalog-skills-route';
import { CatalogUploadRoute } from '@/routes/admin/catalog/catalog-upload-route';
import { CampaignsListRoute } from '@/routes/admin/campaigns/campaigns-list-route';
import { CampaignZoneLayoutRoute } from '@/routes/admin/campaigns/campaign-zone-layout-route';
import { CampaignDetailRoute } from '@/routes/admin/campaigns/campaign-detail-route';
import { CampaignMonitoringRoute } from '@/routes/admin/campaigns/campaign-monitoring-route';
import { CampaignRelanceJournalRoute } from '@/routes/admin/campaigns/campaign-relance-journal-route';
import { OperationalViewProfilsRoute } from '@/routes/admin/operational-view/operational-view-profils-route';
import { OperationalViewFournisseursRoute } from '@/routes/admin/operational-view/operational-view-fournisseurs-route';
import { RankClientEvolutionRoute } from '@/routes/admin/campaigns/rank-client-evolution-route';
import { CampaignAnalysisRoute } from '@/routes/admin/campaigns/campaign-analysis-route';
import { PublicationRoute } from '@/routes/admin/campaigns/publication-route';
import { NegotiationRoute } from '@/routes/admin/campaigns/negotiation-route';
import { CampaignAuditRoute } from '@/routes/admin/campaigns/campaign-audit-route';
import type { RankCode } from '@/api/rank-admin-client';
import type {
  NegotiationEntryStateDto,
  NegotiationHistoryFilterDto,
} from '@/api/negotiation-client';
import type { RankingSortColumn, SortDirection } from '@/lib/analysis-fusion';
import {
  isCampaignStatusBucket,
  type CampaignStatusBucket,
} from '@/lib/campaign-labels';
import { ExportsScreen } from '@/routes/admin/exports/exports-screen';
import { ComplianceAuditScreen } from '@/routes/admin/compliance/compliance-audit-screen';
import {
  CatalogReferentialRoute,
  validateCatalogReferentialSearch,
} from '@/routes/supplier/catalog/catalog-referential-route';
import { ParametersRoute } from '@/routes/parameters/parameters-route';
import { LandingScreen } from '@/routes/onboarding/landing-screen';
import { ActivationRoute } from '@/routes/onboarding/activation-route';
import { MagicLinkRoute } from '@/routes/onboarding/magic-link-route';
import { CguRoute } from '@/routes/cgu/cgu-route';
import { SaisieRoute } from '@/routes/supplier/rate-collection/saisie-route';
import { SupplierDashboardRoute } from '@/routes/supplier/dashboard/dashboard-route';
import { SupplierHistoryRoute } from '@/routes/supplier/history/history-route';
import { SupplierFaqRoute } from '@/routes/supplier/faq/faq-route';
import {
  SupplierCampaignsRoute,
  validateSupplierCampaignsSearch,
} from '@/routes/supplier/campaigns/supplier-campaigns-route';
import { SupplierTeamRoute } from '@/routes/supplier/team/team-route';
import {
  AdminConversationsRoute,
  validateAdminConversationsSearch,
} from '@/routes/admin/communication/admin-conversations-route';
import {
  AnnouncementsRoute,
  validateAnnouncementsSearch,
} from '@/routes/admin/communication/announcements-route';
import {
  KbAdminRoute,
  validateKbAdminSearch,
} from '@/routes/admin/communication/kb-admin-route';
import {
  SupplierEchangesRoute,
  validateSupplierEchangesSearch,
} from '@/routes/supplier/communication/supplier-echanges-route';

// =============================================================================
// router.tsx — Configuration TanStack Router (URL state, DDD §5.2).
// =============================================================================
// « / » → LANDING admin publique (§2.1 : identité produit + baseline + FOOTER +
// CTA de connexion vers `GET /admin/auth/login`). Remplace l'ancien PingScreen.
// « /parameters » → écran A-24 (paramètres applicatifs, S2). L'état de sélection
// vit dans l'URL (search param `param`, RULE_REF_006 : pas de localStorage pour
// l'état métier).

// SEC-NAV-01 : le chrome (header tenant + Sidebar) est gaté par le ROUTAGE, jamais par un flag
// d'auth fabriqué (auth-context reste l'interim admin, GATE P-ROLE-INTERIM=a). La frontière est
// « le routage post-authentification » (DBU §1.4.1 l.374) : les écrans PRÉ-AUTH (landing,
// onboarding, activation, magic-link, signature CGU) s'affichent NUS (aucune nav, aucun header
// tenant) ; tout autre écran monte l'AppShell. Le gating par layout route TanStack a été écarté
// car un id de route pathless préfixe les fullPath des enfants (casse les getRouteApi existants) ;
// la lecture du pathname depuis l'état du routeur est la frontière de routage équivalente.
// DBU §2.1 l.550 / §3.1 l.2350 : « Aucune navigation latérale n'est disponible avant authentification ».
// Le prédicat de path public est la SOURCE UNIQUE partagée (auth/public-paths.ts) : le même prédicat
// gouverne le gating du chrome (ici, via le pathname du routeur) ET les gardes anti-loop des redirects
// (auth-bootstrap.tsx, providers.tsx intercepteur 401), sans dérive possible entre les deux.
const RootChrome = (): React.JSX.Element => {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const auth = useAuth();
  // BUGFIX 2026-09-12 : les pages LÉGALES (/cgu, /mentions-legales) sont publiques PRÉ-AUTH (signature /
  // onboarding, sans chrome) MAIS restent accessibles depuis le FOOTER par un utilisateur AUTHENTIFIÉ —
  // dans ce cas elles doivent CONSERVER la chrome (sidebar + footer), sinon la nav disparaît. On garde le
  // « nu » pour les VRAIS écrans pré-auth (landing, onboarding) et pour ces pages tant qu'aucune session
  // n'est résolue (rôle indéfini). `isPublicPath` reste inchangé (source partagée des gardes anti-loop).
  const authenticated = auth.role !== undefined;
  const isLegalDoc =
    pathname === '/cgu' || pathname === '/mentions-legales';
  const bare = isPublicPath(pathname) && !(authenticated && isLegalDoc);
  return bare ? (
    <Outlet />
  ) : (
    <AppShell>
      <Outlet />
    </AppShell>
  );
};

const rootRoute = createRootRoute({ component: RootChrome });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: AdminLandingScreen,
});

// « /mentions-legales » → page légale publique (footer landing, §2.1 l.562). Pré-auth, sans chrome.
const mentionsLegalesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mentions-legales',
  component: MentionsLegalesScreen,
});

// « /admin » → écran A-02 (tableau de bord administrateur, shell S3/D4).
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminDashboardRoute,
});

/**
 * Search params d'URL du registre fournisseurs (T2-REGISTRE) : recherche `q`, filtres `state[]`
 * (statut multi) / `from` / `to` (période) / `campaign` (résolu en `sirets`), tri `sort`/`dir`,
 * pagination `page`/`pageSize`. État de VUE porté par l'URL (RULE_REF_006 : pas de localStorage).
 * Défauts (q vide, aucun filtre, tri siret asc, page 1, pageSize 25) NON sérialisés → URL propre.
 */
interface SuppliersListSearch {
  readonly q?: string;
  readonly state?: readonly EstablishmentStateDto[];
  readonly tokenStatus?: readonly TokenActivationStatusDto[];
  readonly from?: string;
  readonly to?: string;
  readonly campaign?: string;
  readonly sort?: AdminListSortColumn;
  readonly dir?: AdminListSortDirection;
  readonly page?: number;
  readonly pageSize?: number;
  /**
   * R22-FICHE-DRAWER : SIRET actuellement ouvert dans le volet latéral de la fiche A-04. État
   * d'URL (RULE_REF_006, pas de localStorage) — deep-linkable, compatible bouton retour navigateur.
   */
  readonly open?: string;
}

// « /admin/suppliers » → A-03 registre fournisseurs (référentiel fournisseur Admin). Registre
// recherchable/filtrable/triable/paginé (T2-REGISTRE) : l'état de vue vit dans les search params.
const suppliersListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/suppliers',
  component: SuppliersListRoute,
  validateSearch: (search: Record<string, unknown>): SuppliersListSearch => {
    const result: {
      q?: string;
      state?: readonly EstablishmentStateDto[];
      tokenStatus?: readonly TokenActivationStatusDto[];
      from?: string;
      to?: string;
      campaign?: string;
      sort?: AdminListSortColumn;
      dir?: AdminListSortDirection;
      page?: number;
      pageSize?: number;
      open?: string;
    } = {};

    const q = search['q'];
    if (typeof q === 'string' && q.length > 0) {
      result.q = q;
    }

    // `state` : accepte une valeur unique ou un tableau ; ne garde que les états connus (allowlist).
    const rawState = search['state'];
    const stateCandidates = Array.isArray(rawState) ? rawState : [rawState];
    const states = stateCandidates.filter(
      (s): s is EstablishmentStateDto =>
        typeof s === 'string' &&
        (ESTABLISHMENT_STATES as readonly string[]).includes(s),
    );
    if (states.length > 0) {
      result.state = states;
    }

    // `tokenStatus` : valeur unique ou tableau ; allowlist stricte {Active,Envoye,Expire} (fail-closed).
    const rawTokenStatus = search['tokenStatus'];
    const tokenStatusCandidates = Array.isArray(rawTokenStatus)
      ? rawTokenStatus
      : [rawTokenStatus];
    const tokenStatuses = tokenStatusCandidates.filter(
      (s): s is TokenActivationStatusDto =>
        typeof s === 'string' &&
        (TOKEN_ACTIVATION_STATUSES as readonly string[]).includes(s),
    );
    if (tokenStatuses.length > 0) {
      result.tokenStatus = tokenStatuses;
    }

    const isoDate = /^\d{4}-\d{2}-\d{2}$/;
    const from = search['from'];
    if (typeof from === 'string' && isoDate.test(from)) {
      result.from = from;
    }
    const to = search['to'];
    if (typeof to === 'string' && isoDate.test(to)) {
      result.to = to;
    }

    const campaign = search['campaign'];
    if (typeof campaign === 'string' && campaign.length > 0) {
      result.campaign = campaign;
    }

    const sort = search['sort'];
    if (
      typeof sort === 'string' &&
      (SUPPLIER_SORT_COLUMNS as readonly string[]).includes(sort)
    ) {
      result.sort = sort as AdminListSortColumn;
    }
    const dir = search['dir'];
    if (dir === 'asc' || dir === 'desc') {
      result.dir = dir;
    }

    const page = Number(search['page']);
    if (Number.isInteger(page) && page > 1) {
      result.page = page;
    }
    const pageSize = Number(search['pageSize']);
    if (
      Number.isInteger(pageSize) &&
      (SUPPLIER_PAGE_SIZES as readonly number[]).includes(pageSize)
    ) {
      result.pageSize = pageSize;
    }

    // R22-FICHE-DRAWER : SIRET ouvert dans le volet latéral (aucune allowlist de format ici — le
    // SIRET est server-set/opaque côté API ; une valeur invalide résout simplement une fiche en erreur).
    const open = search['open'];
    if (typeof open === 'string' && open.length > 0) {
      result.open = open;
    }

    return result;
  },
});

// « /admin/suppliers/new » → A-05 enrôlement (2 actes UI). Path statique : doit
// primer sur le path dynamique $siret (TanStack range le statique en premier).
const supplierEnrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/suppliers/new',
  component: SupplierEnrollRoute,
});

// « /admin/suppliers/$siret » → A-04 fiche fournisseur (SIRET = ressource d'URL).
const supplierDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/suppliers/$siret',
  component: SupplierDetailRoute,
});

// « /admin/legal-entities/$siren » → T7-MULTI-ETAB : vue GROUPÉE des établissements d'un SIREN
// (personne morale). Segment TOP-LEVEL DISTINCT de `/admin/suppliers/*` (aucune interaction avec le
// ranking `$siret` vs `new` — plan ARCH §2.2). LECTURE SEULE (GATE_HUMAN T7 : vue + navigation). Le
// SIREN est la ressource d'URL (server-set, validé `^\d{9}$` côté back).
const legalEntityHierarchyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/legal-entities/$siren',
  component: LegalEntityHierarchyRoute,
});

// « /admin/cgu/signatures » → DBU §2.9 registre des signatures + non-signataires (S5bis).
// Placeholder pré-câblé en Étape 0 (rempli par le stream S5bis). CONSERVÉ (deep-links existants).
const cguSignaturesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/cgu/signatures',
  component: CguSignaturesRoute,
});

/**
 * Search params d'URL du registre CGU (A-CGU ÉTAPE 2) : `tab` = onglet actif
 * (versions/detail/comparaison/journal ; « versions » = défaut non sérialisé) ; `version` = id de la
 * version sélectionnée (onglet Détail). État de VUE porté par l'URL (RULE_REF_006 : pas de localStorage).
 */
interface CguRegistreSearch {
  readonly tab?: CguRegistreTab;
  readonly version?: string;
}

const CGU_TABS: readonly CguRegistreTab[] = [
  'versions',
  'detail',
  'comparaison',
  'journal',
];

// « /admin/cgu » → REGISTRE des versions CGU à 4 onglets (A-CGU ÉTAPE 2 + R15-GOV-05). MONDE ADMIN
// d'ÉCRITURE (AdminSessionGuard) : registre + détail/éditeur + comparaison + journal. Path statique
// en un segment ; le
// path plus long `/admin/cgu/signatures` reste distinct (TanStack range le statique le plus long).
const cguRegistreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/cgu',
  component: CguRegistreRoute,
  validateSearch: (search: Record<string, unknown>): CguRegistreSearch => {
    const result: { tab?: CguRegistreTab; version?: string } = {};
    const tab = search['tab'];
    if (typeof tab === 'string' && (CGU_TABS as readonly string[]).includes(tab)) {
      result.tab = tab as CguRegistreTab;
    }
    const version = search['version'];
    if (typeof version === 'string' && version.length > 0) {
      result.version = version;
    }
    return result;
  },
});

/**
 * Search params d'URL du Référentiel Clients (Uc-A-CLIENTS §2.3) : recherche `q`,
 * filtres `statut`/`secteur`, tri `tri`/`sens`, `page`. État de VUE porté par l'URL
 * (RULE_REF_006 : pas de localStorage). Défauts (q vide, filtres « all », tri
 * raisonSociale asc, page 1) NON sérialisés → URL propre.
 */
interface ClientsListSearch {
  readonly q?: string;
  readonly statut?: ClientStateDto;
  readonly secteur?: string;
  readonly tri?: ClientSortColumn;
  readonly sens?: ClientSortDirection;
  readonly page?: number;
  /** Mode création révélé (Bug #3, pattern FOURNISSEUR) ; absent = liste seule. */
  readonly nouveau?: boolean;
}

// « /admin/clients » → DBU §2.3 Référentiel Clients (S6, BC client-reference).
// Registre triable/filtrable/paginé (Uc-A-CLIENTS) : l'état de vue vit dans les search params.
const clientsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/clients',
  component: ClientsListRoute,
  validateSearch: (search: Record<string, unknown>): ClientsListSearch => {
    const result: {
      q?: string;
      statut?: ClientStateDto;
      secteur?: string;
      tri?: ClientSortColumn;
      sens?: ClientSortDirection;
      page?: number;
      nouveau?: boolean;
    } = {};
    const q = search['q'];
    if (typeof q === 'string' && q.length > 0) {
      result.q = q;
    }
    const statut = search['statut'];
    if (statut === 'Actif' || statut === 'Archive') {
      result.statut = statut;
    }
    const secteur = search['secteur'];
    if (typeof secteur === 'string' && secteur.length > 0) {
      result.secteur = secteur;
    }
    const tri = search['tri'];
    if (
      typeof tri === 'string' &&
      (CLIENT_SORT_COLUMNS as readonly string[]).includes(tri)
    ) {
      result.tri = tri as ClientSortColumn;
    }
    const sens = search['sens'];
    if (sens === 'asc' || sens === 'desc') {
      result.sens = sens;
    }
    const page = Number(search['page']);
    if (Number.isInteger(page) && page > 1) {
      result.page = page;
    }
    // `nouveau` (mode création) : accepte booléen JSON ou chaîne « true »/« 1 » (deep-link).
    const nouveau = search['nouveau'];
    if (nouveau === true || nouveau === 'true' || nouveau === '1') {
      result.nouveau = true;
    }
    return result;
  },
});

/** Search param d'URL de l'écran A-24 : `param` = clé du paramètre sélectionné. */
interface ParametersSearch {
  readonly param?: string;
}

// « /admin/catalog/versions » → A-08 browser des versions du catalogue (S8-D2,
// BC catalog). Monde ADMIN (P-05), lecture seule. Path statique en deux segments :
// pas de path dynamique concurrent sous /admin/catalog.
const catalogVersionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/catalog/versions',
  component: CatalogVersionsRoute,
});

// « /admin/catalog/versions/$catalogVersionId/edit » → A-09 édition profil + matrice de
// corrélation (S9-D4, BC catalog). MONDE ADMIN d'ÉCRITURE (P-05), distinct des lectures.
// La version DRAFT éditée est la ressource d'URL (autorité inv.2 = API).
const catalogEditionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/catalog/versions/$catalogVersionId/edit',
  component: CatalogEditionRoute,
  validateSearch: validateCatalogEditionSearch,
});

// « /admin/catalog/skills » → A-25 référentiel de compétences (S9-D4). MONDE ADMIN
// d'ÉCRITURE (P-05) : liste + create + édit + familles + archive.
const catalogSkillsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/catalog/skills',
  component: CatalogSkillsRoute,
});

// « /admin/catalog/uploads » → A-10 upload Excel + dry-run (S9-D4). MONDE ADMIN
// d'ÉCRITURE (P-05) : validation à blanc SANS création de version, DRAFT à la confirmation.
const catalogUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/catalog/uploads',
  component: CatalogUploadRoute,
});

/**
 * Search params d'URL de la liste campagnes (A-12) : `statut` = filtre par statut
 * opérationnel (bucket métier ; état d'URL, pas de localStorage — RULE_REF_006).
 * Calqué sur CampaignAnalysisSearch. Défaut (absent) = « tous » (aucun filtrage).
 */
interface CampaignsListSearch {
  readonly statut?: CampaignStatusBucket;
}

// « /admin/campaigns » → A Campagnes (S10-D4, BC campaign). MONDE ADMIN d'ÉCRITURE
// (AdminSessionGuard) : liste + création (intitulé seul, figement catalog_version_id).
// Filtre statut opérationnel (A-12, S20-REMEDIATION) porté par le search param `statut`.
const campaignsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/campaigns',
  component: CampaignsListRoute,
  validateSearch: (search: Record<string, unknown>): CampaignsListSearch => {
    const statut = search['statut'];
    // « tous » est le défaut implicite : on ne le sérialise pas dans l'URL.
    if (isCampaignStatusBucket(statut) && statut !== 'tous') {
      return { statut };
    }
    return {};
  },
});

// « /admin/campaigns/rank-client-evolution » → écran ADMIN « Référencement / Évolution par client » (R25,
// PHASE R). SURFACE UI DISTINCTE (C3) : zone admin NOMINATIVE (Rang), JAMAIS le panneau ANONYME. Route à
// SEGMENT STATIQUE, enfant DIRECT de rootRoute (sœur de campaignsListRoute). Le segment statique
// « rank-client-evolution » est prioritaire sur le param « $campaignId » de la zone (TanStack : static >
// dynamic) — pas de collision. Périmètre client (SIREN) résolu FRONT (C5) ; aucun path param.
const rankClientEvolutionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/campaigns/rank-client-evolution',
  component: RankClientEvolutionRoute,
});

// « /admin/campaigns/$campaignId » → LAYOUT PARENT de la zone campagne (DBU §2.5 l.1057-1059). Route
// intermédiaire non-pathless (path concret, PAS un id pathless — un id pathless préfixerait les ids des
// enfants et casserait les getRouteApi existants ; réf. note SEC-NAV-01 supra) : elle porte le
// <CampaignCycleNavigator> PERMANENT + l'<Outlet/> des pages de la zone. Toutes les pages de la zone sont
// désormais ses enfants (paths RELATIFS) : les fullPath restent IDENTIQUES (id layout = fullPath layout =
// « /admin/campaigns/$campaignId » ; chaque enfant conserve « /admin/campaigns/$campaignId/<segment> »),
// donc aucun getRouteApi ni aucune cible `to` de la zone ne change (le détail passe en enfant INDEX, cf.
// campaign-detail-route). L'id est la ressource d'URL (autorité API).
const campaignZoneLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/campaigns/$campaignId',
  component: CampaignZoneLayoutRoute,
});

// Détail campagne (scope clients/profils/participations, planning cycles, clonage) : enfant INDEX du
// layout (path '/') → matche EXACTEMENT « /admin/campaigns/$campaignId ». La cible `to` reste
// « /admin/campaigns/$campaignId » (trailing slash trimé) ; seul l'id de la route gagne un « / » final
// (getRouteApi ajusté dans campaign-detail-route). L'id est la ressource d'URL (autorité API).
const campaignDetailRoute = createRoute({
  getParentRoute: () => campaignZoneLayoutRoute,
  path: '/',
  component: CampaignDetailRoute,
});

// L4 (GATE_HUMAN 2026-09-17, Q6 OUI) : l'ex-écran A-14 « /target-prices » est supprimé ; la saisie
// des Prix Cibles se fait dans la carte « Prix cibles » du hub « Détail » (target-prices-panel).
// Une URL résiduelle (favori) tombe sur le « not found » par défaut de TanStack Router.

// « /admin/campaigns/$campaignId/monitoring » → Suivi de campagne temps réel (§2.5, concern A4).
// Enfant du layout de zone (path relatif) ; fullPath inchangé. Câble le read-model de suivi
// existant (health/cards/profile-matrix, AdminSessionGuard) — santé + alertes proactives + progression
// par profil, polling TanStack Query. campaignId = ressource d'URL (autorité API).
const campaignMonitoringRoute = createRoute({
  getParentRoute: () => campaignZoneLayoutRoute,
  path: 'monitoring',
  component: CampaignMonitoringRoute,
});

// « /admin/campaigns/$campaignId/reminders-journal » → Journal des relances (R15-COM-05, DBU §2.7.1).
// Enfant du layout de zone (path relatif) ; fullPath inchangé. Câble le read-model journal
// (GET .../relances, AdminSessionGuard) — historique des relances émises, filtrable/triable.
// campaignId = ressource d'URL (autorité API).
const campaignRelanceJournalRoute = createRoute({
  getParentRoute: () => campaignZoneLayoutRoute,
  path: 'reminders-journal',
  component: CampaignRelanceJournalRoute,
});

// « /admin/campaigns/$campaignId/operational-view/profils » → Page Profils (§2.5 v2.2 / S14-bis).
// Remplace l'ancienne « Vue opérationnelle » (modèle 4 couleurs RETIRÉ). Read-model au vol §2.5 v2.2
// (cards / matrice Profil×Niveau / détail couple). Enfant du layout de zone (path relatif) ; fullPath
// inchangé. La page Fournisseurs vient à « /operational-view/fournisseurs » (C2).
const operationalViewProfilsRoute = createRoute({
  getParentRoute: () => campaignZoneLayoutRoute,
  path: 'operational-view/profils',
  component: OperationalViewProfilsRoute,
});

// « /admin/campaigns/$campaignId/operational-view/fournisseurs » → Page Fournisseurs (§2.5 v2.2 / C2).
// Remplace l'ancienne « Grille détaillée ». Liste fournisseurs invités + détail par couple. Page sœur
// de Profils (navigation croisée dans l'en-tête). Enfant du layout de zone (path relatif) ; fullPath
// inchangé. campaignId = ressource d'URL (autorité API).
const operationalViewFournisseursRoute = createRoute({
  getParentRoute: () => campaignZoneLayoutRoute,
  path: 'operational-view/fournisseurs',
  component: OperationalViewFournisseursRoute,
});

// « /admin/campaigns/$campaignId/rank » — ROUTE SUPPRIMÉE (lot U1, ADR-FUSION-ANALYSE-RANG-01,
// décision 1 : « la page fusionnée remplace /rank (route et onglet « Référencement / Rang »
// supprimés) »). Le tableau de travail, le tag, le figement et l'historique des versions figées
// vivent désormais dans « /admin/campaigns/$campaignId/analysis ».

/**
 * Lit un filtre MULTI-VALEURS de la search — même convention que `state`/`tokenStatus` du registre
 * fournisseurs, étendue à la zone campagne par le lot F1 (GATE_HUMAN 2026-09-21, « sélection
 * multiple par cases à cocher »). L'ENCODAGE sur le fil est celui du sérialiseur de TanStack Router,
 * qui rend une liste en une valeur de paramètre unique ; ce qui compte ici est le CONTRAT : une clé
 * absente vaut « tous », et une liste survit à un aller-retour d'URL.
 *
 * RÉTRO-COMPATIBILITÉ : une URL existante à valeur UNIQUE (`?profil=X`) est acceptée telle quelle et
 * PROMUE en liste d'un élément — aucun lien partagé avant ce lot ne se casse. Les entrées vides ou
 * non-chaînes sont écartées ; une liste vide rend `undefined`, ce qui fait DISPARAÎTRE la clé de
 * l'URL (paramètre absent = tous, jamais de valeur sentinelle).
 *
 * ATTENTION : omettre une clé ici ne la RETIRE PAS de la search vue par le composant (la route
 * racine n'a pas de validation, sa search brute est fusionnée en dessous). Les adaptateurs de route
 * repassent donc les valeurs par `lib/search-list.ts` avant de les donner à l'écran.
 */
function repeatedText(
  search: Record<string, unknown>,
  key: string,
): readonly string[] | undefined {
  const raw = search[key];
  const candidates = Array.isArray(raw) ? raw : [raw];
  const values = candidates.filter(
    (value): value is string => typeof value === 'string' && value.length > 0,
  );
  return values.length > 0 ? values : undefined;
}

/**
 * Search params d'URL de la page Analyse FUSIONNÉE (lot U1, ADR-FUSION-ANALYSE-RANG-01, décision 11) :
 * `profil` · `sousFamille` · `fournisseur` (SIRET) · `topN` (stepper, défaut 3) · `tag` (chip filtrante)
 * · `tri` + `dir` (colonne et sens de tri). Les anciens `seniorite`, `stack`, `tjmMin`/`tjmMax` et `mode`
 * sont SUPPRIMÉS (filtres retirés, sélecteur de mode et nuage de points supprimés). Tout l'état de vue
 * vit dans l'URL (pas de localStorage — RULE_REF_006) : une vue filtrée est partageable et rejouable.
 *
 * LOT F1 (2026-09-21) : `profil`, `sousFamille` et `fournisseur` portent des LISTES.
 */
interface CampaignAnalysisSearch {
  readonly profil?: readonly string[];
  readonly sousFamille?: readonly string[];
  readonly fournisseur?: readonly string[];
  readonly topN?: number;
  readonly tag?: RankCode;
  readonly tri?: RankingSortColumn;
  readonly dir?: SortDirection;
}

/** Échelle de rang FERMÉE des chips filtrantes (fail-closed : toute autre valeur est ignorée). */
const RANK_VALUES: readonly RankCode[] = ['A', 'B', 'PROSPECT', 'NonReferenced'];

/** Colonnes triables du ranking (fail-closed : une colonne inconnue retombe sur le tri par défaut). */
const SORT_COLUMN_VALUES: readonly RankingSortColumn[] = [
  'profil',
  'seniorite',
  'tjmC1',
  'tjmC2',
  'ecartMediane',
  'ecartCible',
  'fournisseur',
  'siret',
  'rang',
  'percentile',
];

// « /admin/campaigns/$campaignId/analysis » → page ADMIN « Analyse » FUSIONNÉE (distribution anonymisée
// + ranking nominatif + tag + figement + export). Segment statique terminal : ne concurrence pas le
// détail $campaignId. campaignId = ressource d'URL (autorité API).
const campaignAnalysisRoute = createRoute({
  getParentRoute: () => campaignZoneLayoutRoute,
  path: 'analysis',
  component: CampaignAnalysisRoute,
  validateSearch: (search: Record<string, unknown>): CampaignAnalysisSearch => {
    const result: {
      profil?: readonly string[];
      sousFamille?: readonly string[];
      fournisseur?: readonly string[];
      topN?: number;
      tag?: RankCode;
      tri?: RankingSortColumn;
      dir?: SortDirection;
    } = {};
    // Filtres MULTI-VALEURS (lot F1) : paramètre répété, valeur unique héritée promue en liste.
    const profil = repeatedText(search, 'profil');
    if (profil !== undefined) {
      result.profil = profil;
    }
    const sousFamille = repeatedText(search, 'sousFamille');
    if (sousFamille !== undefined) {
      result.sousFamille = sousFamille;
    }
    const fournisseur = repeatedText(search, 'fournisseur');
    if (fournisseur !== undefined) {
      result.fournisseur = fournisseur;
    }
    // TOP N : entier FINI uniquement (rejette NaN / chaîne vide) ; le bornage vit dans clampTopN.
    const topN = Number(search['topN']);
    if (search['topN'] !== undefined && search['topN'] !== '' && Number.isFinite(topN)) {
      result.topN = topN;
    }
    const tag = search['tag'];
    if (typeof tag === 'string' && (RANK_VALUES as readonly string[]).includes(tag)) {
      result.tag = tag as RankCode;
    }
    const tri = search['tri'];
    if (typeof tri === 'string' && (SORT_COLUMN_VALUES as readonly string[]).includes(tri)) {
      result.tri = tri as RankingSortColumn;
    }
    const dir = search['dir'];
    if (dir === 'asc' || dir === 'desc') {
      result.dir = dir;
    }
    return result;
  },
});

// « /admin/campaigns/$campaignId/publication » → écran ADMIN « Publication du benchmark » (S18 / stream C).
// Segment statique terminal : ne concurrence pas le détail $campaignId. Valider (Disponible→Validée) + aperçu
// ANONYMISÉ (Cx-S18-5) + publier vers Pulse (PP-08, double-confirm). campaignId = ressource d'URL (autorité API).
const publicationRoute = createRoute({
  getParentRoute: () => campaignZoneLayoutRoute,
  path: 'publication',
  component: PublicationRoute,
});

/**
 * Search params d'URL de la page « Négociation » REFONDUE (lot N4,
 * `tasks/spec-refonte-negociation-20260917.md`) : `fournisseur` (SIRET) · `profil` (code) · `etat` (l'un
 * des 4 états ACTUELS) · `verrouillees` (case « Afficher les verrouillées ») · `hist` (onglet de
 * l'historique) · `page` (page de l'historique). Tout l'état de vue vit dans l'URL (aucun localStorage —
 * RULE_REF_006) : une vue filtrée est partageable et rejouable à l'identique.
 */
interface CampaignNegotiationSearch {
  readonly fournisseur?: readonly string[];
  readonly profil?: readonly string[];
  readonly etat?: readonly NegotiationEntryStateDto[];
  readonly verrouillees?: boolean;
  readonly hist?: NegotiationHistoryFilterDto;
  readonly page?: number;
}

/** Échelle FERMÉE des 4 états métier (fail-closed : toute autre valeur est ignorée). */
const NEGOTIATION_STATE_VALUES: readonly NegotiationEntryStateDto[] = [
  'ModifAutorisee',
  'EnCoursModif',
  'Modifie',
  'Verrouille',
];

/** Onglets de l'historique acceptés par la route (fail-closed ; « Tous » = valeur par défaut, absente). */
const NEGOTIATION_HISTORY_VALUES: readonly NegotiationHistoryFilterDto[] = [
  'Tous',
  'Ouverture',
  'Resaisie',
  'Verrouillage',
  'Relance',
];

// « /admin/campaigns/$campaignId/negotiation » → page ADMIN « Négociation » (refonte N4 : suivi par
// LIGNE, verrouillage au grain fournisseur, historique de négociation). Segment statique terminal : ne
// concurrence pas le détail $campaignId. L'OUVERTURE d'une négociation se fait depuis la vue
// opérationnelle Fournisseurs (détail du fournisseur, décision GATE_HUMAN du 2026-09-22) : cette page
// ne la porte pas. campaignId = ressource d'URL (autorité API).
const negotiationRoute = createRoute({
  getParentRoute: () => campaignZoneLayoutRoute,
  path: 'negotiation',
  component: NegotiationRoute,
  validateSearch: (search: Record<string, unknown>): CampaignNegotiationSearch => {
    const result: {
      fournisseur?: readonly string[];
      profil?: readonly string[];
      etat?: readonly NegotiationEntryStateDto[];
      verrouillees?: boolean;
      hist?: NegotiationHistoryFilterDto;
      page?: number;
    } = {};
    // Filtres MULTI-VALEURS (lot F1) : paramètre répété, valeur unique héritée promue en liste.
    const fournisseur = repeatedText(search, 'fournisseur');
    if (fournisseur !== undefined) {
      result.fournisseur = fournisseur;
    }
    const profil = repeatedText(search, 'profil');
    if (profil !== undefined) {
      result.profil = profil;
    }
    // `etat` : allowlist stricte des 4 états métier (fail-closed) appliquée à CHAQUE valeur répétée.
    const etats = (repeatedText(search, 'etat') ?? []).filter(
      (value): value is NegotiationEntryStateDto =>
        (NEGOTIATION_STATE_VALUES as readonly string[]).includes(value),
    );
    if (etats.length > 0) {
      result.etat = etats;
    }
    // La case n'est PRÉSENTE dans l'URL que lorsqu'elle est cochée (défaut = liste bornée aux ouvertes).
    const verrouillees = search['verrouillees'];
    if (verrouillees === true || verrouillees === 'true') {
      result.verrouillees = true;
    }
    const hist = search['hist'];
    if (
      typeof hist === 'string' &&
      (NEGOTIATION_HISTORY_VALUES as readonly string[]).includes(hist)
    ) {
      result.hist = hist as NegotiationHistoryFilterDto;
    }
    // Page 1-indexée, entière et FINIE (rejette NaN / chaîne vide) ; 1 est le défaut et reste absent.
    const page = Number(search['page']);
    if (Number.isInteger(page) && page > 1) {
      result.page = page;
    }
    return result;
  },
});

// « /admin/campaigns/$campaignId/audit » → page « Audit » de la zone d'analyse campagne (LANE-E-CAMPAIGN-
// AUDIT, A0R-9). Segment statique terminal : ne concurrence pas le détail $campaignId. Read-model ADMIN
// read-only listant les actions auditées (transitions d'état/exports/publications/figements RANG, DÉJÀ
// émises) filtrées par campaign_id, avec signalement des actions IRRÉVERSIBLES (A0R-11). Scope ADMIN =
// pas de filtrage tenant applicatif. campaignId = ressource d'URL (autorité API).
const campaignAuditRoute = createRoute({
  getParentRoute: () => campaignZoneLayoutRoute,
  path: 'audit',
  component: CampaignAuditRoute,
});

// « /admin/exports » → exports CSV référentiel (S10-D4). γ SYNCHRONE : téléchargements
// directs (GET → CSV), pas de job/polling, pas de Domain Event.
const exportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/exports',
  component: ExportsScreen,
});

// « /admin/compliance/audit » → R15-GOV-06 (DBU §1.4.4 / ADR D12) : export avancé compliance du journal
// d'audit (document JSON structuré filtré par période/type). γ SYNCHRONE : téléchargement direct (GET →
// JSON), pas de job/polling, pas de Domain Event. Endpoint admin analytics /admin/audit/export.json.
const complianceAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/compliance/audit',
  component: ComplianceAuditScreen,
});

// « /catalog » → Uc-CAT-PROFILS consultation du RÉFÉRENTIEL des profils de la version publiée
// courante (DBU §3.4, BC catalog). Monde FOURNISSEUR (P-05) : URL distincte de l'admin, lecture
// seule, aucun bouton d'écriture / export. L'état de vue (famille / sousFamille / niveau / q /
// profil) est porté par l'URL (search params ; RULE_REF_006 : pas de localStorage).
const currentCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catalog',
  component: CatalogReferentialRoute,
  validateSearch: validateCatalogReferentialSearch,
});

const parametersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/parameters',
  component: ParametersRoute,
  validateSearch: (search: Record<string, unknown>): ParametersSearch => {
    const param = search['param'];
    return typeof param === 'string' && param.length > 0 ? { param } : {};
  },
});

// « /onboarding » → A-01 Landing : saisie e-mail → accusé « lien envoyé »
// (UC-F-001 ; envoi e-mail stubé, Postmark = S7). Le lien e-mail pointe vers
// « /onboarding/$token » (F-01) qui consomme le magic-link (usage unique strict).
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: LandingScreen,
});

// « /onboarding/activate/$token » → ACTIVATION INITIALE du compte fournisseur (S0-ACTIVATION,
// F1 = A : route DISTINCTE du magic-link). Consomme l'ActivationToken (l'API active le credential
// ET ouvre la session, F2), confirme les coordonnées (affichage seul, F4) puis enchaîne vers la CGU.
// Segment statique `activate` : prime sur le path dynamique $token (TanStack range le statique avant).
const activationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/activate/$token',
  component: ActivationRoute,
});

const magicLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/$token',
  component: MagicLinkRoute,
});

// « /cgu » → F-02 Onboarding + CGU : affichage du contenu → signature débloquée
// après lecture (intentionnalité par lecture, UC-F-002 E1) → grille.
const cguRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cgu',
  component: CguRoute,
});

/**
 * Search params d'URL de l'écran de saisie : `famille` = famille de profils filtrée (OPP-03) ;
 * `cycle` = numéro de cycle d'affichage (F-10 visible en Cycle 2 — état d'URL, pas de localStorage) ;
 * `profil` = R15-CMP-03 : profil deep-linké depuis la fiche catalogue → sa cellule est focalisée à
 * l'ouverture (état d'URL, RULE_REF_006 : jamais localStorage).
 */
interface SaisieSearch {
  readonly famille?: string;
  readonly cycle?: number;
  readonly profil?: string;
}

// « /saisie/$submissionId » → écran de SAISIE FOURNISSEUR (DDD §3.6, S12). La submission est
// la ressource d'URL ; le filtre famille (OPP-03) et le cycle (F-10, S17) sont en search params
// (URL state ; RULE_REF_006 : pas de localStorage pour l'état métier/vue).
const saisieRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/saisie/$submissionId',
  component: SaisieRoute,
  validateSearch: (search: Record<string, unknown>): SaisieSearch => {
    const result: { famille?: string; cycle?: number; profil?: string } = {};
    const famille = search['famille'];
    if (typeof famille === 'string' && famille.length > 0) {
      result.famille = famille;
    }
    const rawCycle = search['cycle'];
    const cycle =
      typeof rawCycle === 'number'
        ? rawCycle
        : typeof rawCycle === 'string' && /^\d+$/.test(rawCycle)
          ? Number(rawCycle)
          : undefined;
    if (cycle !== undefined && Number.isInteger(cycle) && cycle > 0) {
      result.cycle = cycle;
    }
    // R15-CMP-03 — profil deep-linké (calque du param `famille` : string non-vide, sinon non sérialisé).
    const profil = search['profil'];
    if (typeof profil === 'string' && profil.length > 0) {
      result.profil = profil;
    }
    return result;
  },
});

// « /dashboard » → TABLEAU DE BORD FOURNISSEUR (DBU §3.2, Lot A / D3b). Point d'entrée post-auth
// fournisseur : sollicitations actives + historique + complétion. Aucun état d'URL (vue statique).
const supplierDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: SupplierDashboardRoute,
});

// « /history » → HISTORIQUE FOURNISSEUR, onglet « Mes participations » (DBU §3.9, Lot A). Sélection de la
// vue détail portée en ÉTAT D'URL (`?campaign=<id>`) : partageable, compatible bouton retour (RULE_REF_006).
const supplierHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/history',
  component: SupplierHistoryRoute,
  validateSearch: (search: Record<string, unknown>): { campaign?: string } => {
    const campaign = search['campaign'];
    return typeof campaign === 'string' && campaign.length > 0
      ? { campaign }
      : {};
  },
});

// « /faq » → FOIRE AUX QUESTIONS FOURNISSEUR (GATE_HUMAN 2026-09-11). Page autonome de « Ma Documentation »,
// EXTRAITE de l'ancienne page « Conditions de référencement » (retirée). Contenu FIXE plateforme
// (faq-content.ts) : aucune donnée serveur, aucun état d'URL.
const supplierFaqRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/faq',
  component: SupplierFaqRoute,
});

// « /campagnes » → MES CAMPAGNES EN COURS (FOURNISSEUR, GATE_HUMAN 2026-09-11). Page liste→détail condensée
// (récap 6 champs + périmètre = catalogue navigable filtré). Réutilise le référentiel catalogue (auto-porté
// dans l'adaptateur). État de vue (statut / campagne sélectionnée / famille / sousFamille / niveau / q /
// profil) porté par les search params (RULE_REF_006 : pas de localStorage). /conditions reste distinct.
const supplierCampaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/campagnes',
  component: SupplierCampaignsRoute,
  validateSearch: validateSupplierCampaignsSearch,
});

// « /collaborateurs » → MON ÉQUIPE / COLLABORATEURS (DBU §3.7, F4). Zone RÉSERVÉE au contact MASTER
// (role-gating porté par l'écran ; ACL réelle côté API/RLS). Annuaire du SIRET + invitation + révocation.
// Aucun état d'URL (vue statique).
const supplierTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/collaborateurs',
  component: SupplierTeamRoute,
});

// « /admin/conversations » → R15-COM-02 CHAT 1-to-1 admin (BC communication). Vue scindée : liste des
// fils (SIRET/campagne/aperçu/non-lus) + fil chronologique + composition texte brut. État de vue (q /
// campagne / non-lus / fil ouvert) porté par les search params (RULE_REF_006 : pas de localStorage).
const adminConversationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/conversations',
  component: AdminConversationsRoute,
  validateSearch: validateAdminConversationsSearch,
});

// « /admin/announcements » → R15-COM-03 « Annonces » (BC communication). MONDE ADMIN d'ÉCRITURE
// (AdminSessionGuard) : composer (titre + éditeur riche + campagne cible + planification) + registre
// filtrable/triable + annulation. État de vue (onglet `tab`, annonce ouverte `announcement`, filtres
// statut/campagne/période) porté par les search params (RULE_REF_006 : pas de localStorage).
const announcementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/announcements',
  component: AnnouncementsRoute,
  validateSearch: validateAnnouncementsSearch,
});

// « /admin/knowledge-base » → R15-COM-04 KB ADMIN (BC communication). Archives consultables des fils
// clôturés, transverses : recherche FTS + filtres (SIRET/campagne/période) + détail LECTURE SEULE +
// tags (taxonomie) + export JSON/CSV (ADMIN ONLY). État de vue (q/campagne/siret/from/to/fil ouvert)
// porté par les search params (RULE_REF_006 : pas de localStorage).
const kbAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/knowledge-base',
  component: KbAdminRoute,
  validateSearch: validateKbAdminSearch,
});

// « /echanges » → CONSOLIDATION FOURNISSEUR « Mes échanges avec JeKo » (GATE_HUMAN 2026-09-11). UNE page
// liste→détail (layout /archives) FUSIONNANT côté client le chat EN COURS (COM-02) et les archives KB
// (COM-04). Détail INTERACTIF pour un fil en cours, LECTURE SEULE pour un fil archivé. État de vue
// (statut/campagne/période/non-lues/fil ouvert) porté par les search params (RULE_REF_006). Route UNIQUE
// des échanges fournisseur (les anciennes /messagerie et /archives ont été retirées, GATE_HUMAN 2026-09-11).
const supplierEchangesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/echanges',
  component: SupplierEchangesRoute,
  validateSearch: validateSupplierEchangesSearch,
});

// La zone campagne « /admin/campaigns/$campaignId » est un SOUS-ARBRE : le layout parent porte le
// navigateur de cycle PERMANENT (§2.5 l.1057-1059) + l'<Outlet/> ; toutes ses pages sont ses enfants
// (détail en INDEX, le reste en segments relatifs). Les fullPath des enfants sont IDENTIQUES à l'ancien
// routage plat — seul le nesting change (aucune régression d'URL / de getRouteApi / de cible `to`).
const campaignZoneLayoutTree = campaignZoneLayoutRoute.addChildren([
  campaignDetailRoute,
  campaignMonitoringRoute,
  campaignRelanceJournalRoute,
  operationalViewProfilsRoute,
  operationalViewFournisseursRoute,
  campaignAnalysisRoute,
  publicationRoute,
  negotiationRoute,
  campaignAuditRoute,
]);

// Toutes les routes hors zone campagne restent enfants DIRECTS de rootRoute (fullPath inchangés) ; le
// chrome est gaté dans le composant racine par le pathname (SEC-NAV-01). La zone campagne est branchée
// via son sous-arbre. Exporté pour les UT (montage sur history mémoire, cf. app-shell-nav-gate.test.tsx
// et campaign-zone-navigator-permanence.test.tsx).
export const routeTree = rootRoute.addChildren([
  indexRoute,
  mentionsLegalesRoute,
  adminRoute,
  suppliersListRoute,
  supplierEnrollRoute,
  supplierDetailRoute,
  legalEntityHierarchyRoute,
  cguRegistreRoute,
  cguSignaturesRoute,
  clientsListRoute,
  catalogVersionsRoute,
  catalogEditionRoute,
  catalogSkillsRoute,
  catalogUploadRoute,
  campaignsListRoute,
  rankClientEvolutionRoute,
  campaignZoneLayoutTree,
  exportsRoute,
  complianceAuditRoute,
  currentCatalogRoute,
  parametersRoute,
  onboardingRoute,
  activationRoute,
  magicLinkRoute,
  cguRoute,
  saisieRoute,
  supplierDashboardRoute,
  supplierHistoryRoute,
  supplierFaqRoute,
  supplierCampaignsRoute,
  supplierTeamRoute,
  adminConversationsRoute,
  announcementsRoute,
  kbAdminRoute,
  supplierEchangesRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
